'use client';

import { useContext } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { Link } from '@/app_components/common/Link';
import { GtmContext } from '@/app_lib/providers/GtmProvider';
import { type TrackEventParams, trackEvent } from '@/app_lib/services/Gtm/utils';
import { cn } from '@/app_lib/utils/utils';

// TODO: Add option for no styling for buttons that are buttons, but look like links?
const regularButton = tv({
  base: 'group/button inline-flex capitalize items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors disabled:cursor-not-allowed disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:pointer-events-none cursor-pointer rounded-full button-shadow-focus outline-none focus-visible:ring-4 leading-none',
  defaultVariants: {
    size: 'md',
    variant: 'primary',
  },
  variants: {
    size: {
      lg: 'button-h-lg button-v-lg typography-default',
      md: 'button-h-md button-v-md typography-default',
      sm: 'button-h-sm button-v-sm typography-xs',
    },
    variant: {
      accent:
        'button-accent button-accent-hover disabled:button-accent-disabled aria-disabled:button-accent-disabled button-text-accent disabled:button-text-accent-disabled aria-disabled:button-text-accent-disabled active:button-accent',
      primary:
        'button-primary button-primary-hover disabled:button-primary-disabled aria-disabled:button-primary-disabled button-text-primary disabled:button-text-primary-disabled aria-disabled:button-text-primary-disabled active:button-primary',
      primaryOutline:
        'button-primary-outline hover:button-primary-outline-hover active:button-primary-outline-active disabled:button-primary-outline-disabled aria-disabled:button-primary-outline-disabled',
      secondary:
        'button-secondary button-secondary-hover disabled:button-secondary-disabled button-text-secondary disabled:button-text-secondary-disabled active:button-secondary aria-disabled:button-secondary-disabled',
      sso: 'border-none bg-neutrals-100 hover:bg-neutrals-200 active:shadow-none',
    },
  },
});

const tertiaryButton = tv({
  base: 'group/button inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors disabled:cursor-not-allowed disabled:pointer-events-none cursor-pointer hover:underline disabled:hover:no-underline aria-disabled:cursor-not-allowed aria-disabled:pointer-events-none aria-disabled:hover:no-underline leading-[120%]',
  defaultVariants: {
    size: 'md',
    variant: 'primary',
  },
  variants: {
    size: {
      lg: '',
      md: 'typography-default',
      sm: 'typography-xs',
    },
    variant: {
      accent:
        'button-tertiary-accent button-tertiary-accent-active disabled:button-tertiary-accent-disabled aria-disabled:button-tertiary-accent-disabled',
      primary:
        'button-tertiary button-tertiary-active disabled:button-tertiary-disabled aria-disabled:button-tertiary-disabled',
      primaryOutline: '',
      secondary: '',
      sso: '',
    },
  },
});

type RegularButtonVariants = VariantProps<typeof regularButton>;
type TertiaryButtonVariants = VariantProps<typeof tertiaryButton>;
type ButtonVariants = RegularButtonVariants & TertiaryButtonVariants;

interface ButtonOwnProps<E extends 'button' | 'a' | typeof Link> extends ButtonVariants {
  children: React.ReactNode;
  className?: string;
  trackingData?: TrackEventParams;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => void;
  as?: E;
  isTertiary?: boolean;
  noCapitalize?: boolean;
}

export type ButtonProps<E extends 'button' | 'a' | typeof Link> = ButtonOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof ButtonOwnProps<E>>;

export type ButtonV2Variants = ButtonVariants;

/**
 * Polymorphic Button component.
 *
 * Renders either:
 * - Our custom Link component by default
 * - A native `<button/>` element when used with `as="button"`
 * - Our Link component is used for  NextLink and anchor tags (use as="a" for anchor tags)
 *
 * This component will not accept any props not associated with it's given element type.
 */
export const ButtonV2 = <E extends 'button' | 'a' | typeof Link = typeof Link>({
  children,
  className,
  as,
  isTertiary = false,
  variant,
  size,
  onClick,
  trackingData,
  noCapitalize = false,
  ...rest
}: ButtonProps<E>) => {
  const { pageData } = useContext(GtmContext);
  // Both NextLinks and Anchor tags need to use our custom Link component
  const Element = (as === 'button' ? as : Link) as React.ElementType;
  // When using an anchor tag, we need to pass the as: 'a' prop to the Link component to render as an anchor tag
  const isAnchor = as === 'a';

  return (
    <Element
      {...(isAnchor && { as: 'a' })}
      className={cn(
        isTertiary ? cn(tertiaryButton({ size, variant })) : regularButton({ size, variant }),
        noCapitalize && 'normal-case',
        className,
      )}
      {...rest}
      onClick={(e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
        // TODO: check with data team to see if we can track failures
        if (trackingData) {
          trackEvent({ ...pageData, ...trackingData.data }, trackingData.eventType);
        }
        onClick?.(e);
      }}
    >
      {children}
    </Element>
  );
};
